import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  forwardRef,
  type ReactNode,
} from "react";
import {
  COLOR_ACCENT,
  COLOR_BORDER_STRONG,
  COLOR_DANGER,
  COLOR_DANGER_BG,
  COLOR_SURFACE,
  COLOR_TEXT_DIM,
  COLOR_TEXT_ON_ACCENT,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_CONTROL,
  RADIUS_SM,
  RADIUS_XS,
} from "./tokens.ts";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "ghost"
  | "link";
export type ButtonSize = "xs" | "sm" | "md";

const SIZE_STYLE: Record<ButtonSize, CSSProperties> = {
  xs: {
    minHeight: 24,
    padding: "4px 8px",
    fontSize: FONT_SIZE_TINY,
    borderRadius: RADIUS_XS,
  },
  sm: {
    minHeight: 30,
    padding: "7px 14px",
    fontSize: FONT_SIZE_SMALL,
    borderRadius: RADIUS_CONTROL,
  },
  md: {
    minHeight: 36,
    padding: "9px 16px",
    fontSize: FONT_SIZE_BODY,
    borderRadius: RADIUS_CONTROL,
  },
};

const VARIANT_STYLE: Record<ButtonVariant, CSSProperties> = {
  primary: {
    border: "none",
    background: COLOR_ACCENT,
    color: COLOR_TEXT_ON_ACCENT,
    fontWeight: 600,
  },
  secondary: {
    border: `0.5px solid ${COLOR_BORDER_STRONG}`,
    background: COLOR_SURFACE,
    color: COLOR_TEXT_DIM,
    fontWeight: 500,
  },
  danger: {
    border: "1px solid rgba(220,60,60,0.25)",
    background: COLOR_DANGER_BG,
    color: COLOR_DANGER,
    fontWeight: 600,
  },
  ghost: {
    border: "none",
    background: "transparent",
    color: COLOR_TEXT_DIM,
    fontWeight: 500,
  },
  link: {
    minHeight: "auto",
    padding: 0,
    border: "none",
    background: "transparent",
    color: COLOR_ACCENT,
    fontWeight: 500,
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "secondary",
      size = "sm",
      fullWidth = false,
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      type = "button",
      style,
      children,
      ...props
    },
    ref,
  ) {
    const inert = disabled || loading;
    return (
      <button
        ref={ref}
        type={type}
        disabled={inert}
        aria-busy={loading || undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          width: fullWidth ? "100%" : undefined,
          cursor: inert ? (loading ? "wait" : "default") : "pointer",
          opacity: inert ? 0.5 : 1,
          fontFamily: "inherit",
          lineHeight: 1.2,
          userSelect: "none",
          whiteSpace: "nowrap",
          ...SIZE_STYLE[size],
          ...VARIANT_STYLE[variant],
          ...style,
        }}
        {...props}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </button>
    );
  },
);

export type IconButtonVariant = Exclude<ButtonVariant, "link">;

const ICON_SIZE: Record<ButtonSize, number> = {
  xs: 24,
  sm: 30,
  md: 36,
};

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: ReactNode;
  label: string;
  variant?: IconButtonVariant;
  size?: number | ButtonSize;
  radius?: number;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      label,
      variant = "ghost",
      size = 24,
      radius,
      disabled = false,
      type = "button",
      title,
      style,
      ...props
    },
    ref,
  ) {
    const controlSize = typeof size === "number" ? size : ICON_SIZE[size];
    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        title={title ?? label}
        disabled={disabled}
        style={{
          width: controlSize,
          height: controlSize,
          minWidth: controlSize,
          minHeight: controlSize,
          padding: 0,
          borderRadius: radius ?? (controlSize <= 22 ? RADIUS_XS : RADIUS_SM),
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          fontFamily: "inherit",
          ...VARIANT_STYLE[variant],
          ...(variant === "ghost" ? { fontWeight: 400 } : undefined),
          ...style,
        }}
        {...props}
      >
        {icon}
      </button>
    );
  },
);
